/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: ServerSettingsForm.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the Provisioning Server Settings module.
----------------------------------------------------------------*/

import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { Server, Info } from 'lucide-react';
import * as api from './api';
import { useNotifications } from '../../contexts/NotificationContext';
import { useI18n } from '../../hooks/useI18n';
import { ModuleFormHeader } from '../../components/layouts';
import { FixedActionBar } from '../../components/ui/FixedActionBar';
import { useContextStore } from '../../store/context';
import { useSaveWithAction } from '../../hooks/useSaveWithAction';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { FormBox } from '../../components/ui/FormBox';
import { SimpleTooltip } from '../../components/ui/SimpleTooltip';
import ConfirmationModal from '../../components/ui/ConfirmationModal';
import Select from '../../components/ui/select';

interface ServerSettingFormData {
    name: string;
    domainId: number;
    serverDomain: string;
    sipPort: number;
    protocol: 'UDP' | 'TCP' | 'TLS';
    httpPort: number;
    httpsPort: number;
    outboundProxy?: string;
    outboundProxyPort?: number;
}

export default function ServerSettingsForm() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { t } = useI18n();
    const { showSuccess, showError } = useNotifications();
    const { selectedDomain } = useContextStore();

    const isEdit = id && id !== 'new';
    const settingId = isEdit ? id! : undefined;

    const { register, trigger, getValues, reset, formState: { errors }, control, watch, setValue } = useForm<ServerSettingFormData>({
        defaultValues: {
            name: '',
            domainId: 0,
            serverDomain: '',
            sipPort: 5060,
            protocol: 'UDP',
            httpPort: 80,
            httpsPort: 443,
            outboundProxy: '',
            outboundProxyPort: undefined
        }
    });

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);

    useEffect(() => {
        if (isEdit && settingId) {
            loadSetting(settingId);
        } else if (!isEdit && selectedDomain) {
            setValue('domainId', selectedDomain.id);
        }
    }, [isEdit, settingId, selectedDomain, setValue]);

    const loadSetting = async (sid: number | string) => {
        try {
            const setting = await api.getServerSetting(sid);
            if (setting) {
                reset({
                    name: setting.name,
                    serverDomain: setting.serverDomain,
                    sipPort: setting.sipPort,
                    protocol: setting.protocol,
                    httpPort: setting.httpPort,
                    httpsPort: setting.httpsPort,
                    outboundProxy: setting.outboundProxy || '',
                    outboundProxyPort: setting.outboundProxyPort
                });
            }
        } catch (error) {
            showError('Failed to load setting details');
            navigate('/settings/provisioning/provisioning-server-settings');
        }
    };

    const handleSaveInternal = async () => {
        const isValid = await trigger();
        if (!isValid) return null;

        const data = getValues();
        setIsSubmitting(true);
        try {
            const payload = {
                ...data,
                outboundProxy: data.outboundProxy || undefined,
                outboundProxyPort: data.outboundProxyPort || undefined
            };

            let savedSetting;
            if (isEdit && settingId) {
                savedSetting = await api.updateServerSetting(settingId, payload);
                if (!savedSetting) savedSetting = { id: settingId, ...payload };
            } else {
                savedSetting = await api.createServerSetting(payload);
            }

            showSuccess(`Setting ${isEdit ? 'updated' : 'created'} successfully`);
            return savedSetting;
        } catch (error) {
            console.error(error);
            showError(`Failed to ${isEdit ? 'update' : 'create'} setting`);
            throw error;
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleSaveWithAction = useSaveWithAction({
        onSave: handleSaveInternal,
        listPath: '/settings/provisioning/provisioning-server-settings',
        newPath: '/settings/provisioning/provisioning-server-settings/new',
        mode: isEdit ? 'edit' : 'create',
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        getEditPath: (savedData: any) => `/settings/provisioning/provisioning-server-settings/${savedData.id || savedData.uuid}`,
        storageKey: 'serverSettings:saveAction'
    });

    const handleBack = () => navigate('/settings/provisioning/provisioning-server-settings');

    const handleCancel = () => {
        if (isEdit) {
            reset();
        } else {
            handleBack();
        }
    };

    const handleDelete = () => setShowDeleteModal(true);

    const confirmDelete = async () => {
        if (!settingId) return;
        try {
            await api.deleteServerSetting(settingId);
            showSuccess('Setting deleted');
            navigate('/settings/provisioning/provisioning-server-settings');
        } catch (e) {
            showError('Failed to delete setting');
        }
    };

    return (
        <div className="p-6 pb-20">
            <ModuleFormHeader
                icon={<Server className="h-5 w-5 text-primary" />}
                title={isEdit ? 'Edit Server Setting' : 'New Server Setting'}
                backButtonText={t('common.list', 'List')}
                onBack={handleBack}
            />

            <div className="mt-8 space-y-6">
                <form>
                    <input type="hidden" {...register('domainId')} />
                    <FormBox
                        title="General Settings"
                        icon={<Info className="h-5 w-5" />}
                    >
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-3 items-center">
                            {/* Line 1: Name and HTTP Port */}
                            <SimpleTooltip content="Name for this server configuration profile">
                                <Label htmlFor="name" required className="text-left">Name</Label>
                            </SimpleTooltip>
                            <Input
                                id="name"
                                {...register('name', { required: 'Name is required' })}
                                placeholder="e.g. Production"
                                error={errors.name?.message}
                            />

                            <SimpleTooltip content="HTTP Port used for provisioning (default 80)">
                                <Label htmlFor="httpPort" required className="text-left">HTTP Port</Label>
                            </SimpleTooltip>
                            <Input
                                id="httpPort"
                                type="number"
                                {...register('httpPort', { required: 'HTTP Port is required', valueAsNumber: true })}
                                placeholder="80"
                                error={errors.httpPort?.message}
                            />

                            {/* Line 2: Server Domain and HTTPS Port */}
                            <SimpleTooltip content="Domain or IP address of the SIP server">
                                <Label htmlFor="serverDomain" required className="text-left">Server Domain</Label>
                            </SimpleTooltip>
                            <Input
                                id="serverDomain"
                                {...register('serverDomain', { required: 'Server Domain is required' })}
                                placeholder="e.g. sip.example.com"
                                error={errors.serverDomain?.message}
                            />

                            <SimpleTooltip content="HTTPS Port used for provisioning (default 443)">
                                <Label htmlFor="httpsPort" required className="text-left">HTTPS Port</Label>
                            </SimpleTooltip>
                            <Input
                                id="httpsPort"
                                type="number"
                                {...register('httpsPort', { required: 'HTTPS Port is required', valueAsNumber: true })}
                                placeholder="443"
                                error={errors.httpsPort?.message}
                            />

                            {/* Line 3: SIP Port and Outbound Proxy */}
                            <SimpleTooltip content="SIP Port (usually 5060)">
                                <Label htmlFor="sipPort" required className="text-left">SIP Port</Label>
                            </SimpleTooltip>
                            <Input
                                id="sipPort"
                                type="number"
                                {...register('sipPort', { required: 'SIP Port is required', valueAsNumber: true })}
                                placeholder="5060"
                                error={errors.sipPort?.message}
                            />

                            <SimpleTooltip content="Optional Outbound Proxy address">
                                <Label htmlFor="outboundProxy" className="text-left">Outbound Proxy</Label>
                            </SimpleTooltip>
                            <Input
                                id="outboundProxy"
                                {...register('outboundProxy')}
                                placeholder="e.g. sbc.example.com"
                            />

                            {/* Line 4: Protocol and Outbound Proxy Port */}
                            <SimpleTooltip content="SIP Transport Protocol">
                                <Label htmlFor="protocol" required className="text-left">Protocol</Label>
                            </SimpleTooltip>
                            <div>
                                <Controller
                                    control={control}
                                    name="protocol"
                                    rules={{ required: 'Protocol is required' }}
                                    render={({ field }) => (
                                        <Select
                                            value={field.value}
                                            onValueChange={field.onChange}
                                            options={[
                                                { value: 'UDP', label: 'UDP' },
                                                { value: 'TCP', label: 'TCP' },
                                                { value: 'TLS', label: 'TLS' },
                                            ]}
                                            placeholder="Select Protocol"
                                        />
                                    )}
                                />
                                {errors.protocol && <p className="text-sm text-red-500 mt-1">{errors.protocol.message}</p>}
                            </div>

                            <SimpleTooltip content="Optional Outbound Proxy Port">
                                <Label htmlFor="outboundProxyPort" className="text-left">Outbound Proxy Port</Label>
                            </SimpleTooltip>
                            <Input
                                id="outboundProxyPort"
                                type="number"
                                {...register('outboundProxyPort', { valueAsNumber: true })}
                                placeholder="5060"
                            />
                        </div>
                    </FormBox>
                </form>
            </div>

            <FixedActionBar
                onSaveWithAction={handleSaveWithAction}
                onCancel={handleCancel}
                onDelete={isEdit ? handleDelete : undefined}
                isEditMode={isEdit}
                isSaving={isSubmitting}
                cancelText={t('common.cancel', 'Cancel')}
                deleteText={t('common.delete', 'Delete')}
                useSaveDropdown={true}
                saveDropdownStorageKey="serverSettings:saveAction"
            />

            <ConfirmationModal
                isOpen={showDeleteModal}
                onClose={() => setShowDeleteModal(false)}
                onConfirm={confirmDelete}
                title="Delete Setting"
                message="Are you sure you want to delete this setting?"
                confirmText="Delete"
                cancelText="Cancel"
                type="danger"
            />
        </div>
    );
}
